import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Chart from "chart.js";

const defaultLineStyle = {
  pointRadius: 0,
  borderWidth: 2,
  showLine: true,
  fill: false,
  steppedLine: true,
  borderColor: "rgba(255,255,255,0)",
};

function SurvivabilityChart({ id, highRiskData, lowRiskData, predictionsData, onChartChange }) {
  const chartRef = useRef(null);
  const [chart, setChart] = useState(null);

  // Initialise the chart on initial render, once chartRef is initialised
  useEffect(() => {
    if (chartRef && chartRef.current) {
      const newChart = new Chart(chartRef.current, {
        type: "scatter",
        data: {
          datasets: [
            /* Predictions */
            {
              label: "Predictions",
              data: predictionsData
                .filter((prediction) => prediction.survivability > 0)
                .map((prediction) => ({
                  x: prediction.time,
                  y: prediction.survivability * 100,
                })),
              ...defaultLineStyle,
              pointRadius: 5,
              showLine: false,
              pointBackgroundColor: "#333",
              backgroundColor: "#333",
              borderColor: "#333",
            },
            ...predictionsData.map((prediction) => ({
              ...defaultLineStyle,
              data:
                prediction.survivability > 0
                  ? [
                      { x: 0, y: prediction.survivability * 100 },
                      { x: prediction.time, y: prediction.survivability * 100 },
                      { x: prediction.time, y: 0 },
                    ]
                  : [],
              borderColor: "rgba(0,0,0,1)",
              borderDash: [5, 5],
              borderWidth: 1,
            })),
            /* Low Risk – Upper confidence interval */
            {
              data: lowRiskData.map((point) => ({
                x: point.t,
                y: point.upper * 100,
              })),
              ...defaultLineStyle,
              fill: "+2",
              backgroundColor: "rgba(0,85,135,0.2)",
            },
            /* Low Risk - Actual */
            {
              label: "Low Risk",
              data: lowRiskData.map((point) => ({
                x: point.t,
                y: point.s * 100,
              })),
              ...defaultLineStyle,
              borderColor: "rgba(0,85,135,1)",
            },
            /* Low Risk - Lower confidence interval */
            {
              data: lowRiskData.map((point) => ({
                x: point.t,
                y: point.lower * 100,
              })),
              ...defaultLineStyle,
            },
            /* High Risk - Upper confidence interval */
            {
              data: highRiskData.map((point) => ({
                x: point.t,
                y: point.upper * 100,
              })),
              ...defaultLineStyle,
              fill: "+2",
              backgroundColor: "rgba(150,0,5,0.2)",
            },
            /* High Risk - Actual */
            {
              label: "High Risk",
              data: highRiskData.map((point) => ({
                x: point.t,
                y: point.s * 100,
              })),
              ...defaultLineStyle,
              borderColor: "rgba(150,0,5,1)",
            },
            /* High Risk - Lower confidence interval */
            {
              data: highRiskData.map((point) => ({
                x: point.t,
                y: point.lower * 100,
              })),
              ...defaultLineStyle,
            },
          ],
        },
        options: {
          maintainAspectRatio: true,
          aspectRatio: 1.5,
          legend: {
            position: "bottom",
            align: "center",
            labels: {
              boxWidth: 6,
              filter: function (item) {
                return (
                  item.datasetIndex === 0 ||
                  item.datasetIndex === 2 + predictionsData.length ||
                  item.datasetIndex === 5 + predictionsData.length
                );
              },
            },
          },
          animation: {
            onComplete: () => {
              onChartChange(id, newChart.toBase64Image());
            },
          },
        },
      });
      setChart(newChart);
    }
  }, [chartRef]);

  // Update the chart on every subsequent render
  useEffect(() => {
    if (chart) {
      chart.data.datasets[0].data = predictionsData
        .filter((prediction) => prediction.survivability > 0)
        .map((prediction) => ({
          x: prediction.time,
          y: prediction.survivability * 100,
        }));
      predictionsData.forEach((prediction, index) => {
        chart.data.datasets[index + 1].data =
          prediction.survivability > 0
            ? [
                { x: 0, y: prediction.survivability * 100 },
                { x: prediction.time, y: prediction.survivability * 100 },
                { x: prediction.time, y: 0 },
              ]
            : [];
      });
      chart.update();
    }
  }, [predictionsData]);

  return <canvas ref={chartRef}></canvas>;
}

const RiskData = PropTypes.shape({
  t: PropTypes.number.isRequired,
  s: PropTypes.number.isRequired,
  lower: PropTypes.number.isRequired,
  upper: PropTypes.number.isRequired,
});

SurvivabilityChart.propTypes = {
  id: PropTypes.string.isRequired,
  highRiskData: PropTypes.arrayOf(RiskData),
  lowRiskData: PropTypes.arrayOf(RiskData),
  predictionsData: PropTypes.arrayOf(
    PropTypes.shape({
      time: PropTypes.number.isRequired,
      survivability: PropTypes.number.isRequired,
    })
  ),
  onChartChange: PropTypes.func.isRequired,
};

export default SurvivabilityChart;
